<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="subtitle" :back="listPath">
            <v-chip v-if="!isCreate && !loadingEntity" small :color="member.approved ? 'success' : 'error'" :outlined="!member.approved">
                {{ member.approved ? 'Approved' : 'Pending approval' }}
            </v-chip>
            <v-btn v-if="isShow && !isDealer" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-title class="section-title">Personal details</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.firstName" :rules="required" label="First name *" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.lastName" :rules="required" label="Last name *" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.fatherName" label="Father's name" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select v-model="member.gender" :items="genders" label="Gender" outlined :clearable="!readOnly" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <date-field v-model="member.birthDate" label="Birth date" :readonly="readOnly" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.profession" label="Profession" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.number" label="Member number" type="number" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.dimotologioNumber" label="Αριθμός Δημοτολογίου" outlined />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-card-title class="section-title">Contact</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.phone" label="Phone" prepend-inner-icon="mdi-phone-outline" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.trim="member.email"
                                :rules="emailRules"
                                label="Email"
                                prepend-inner-icon="mdi-email-outline"
                                outlined
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.address" label="Address" outlined />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.trim="member.postalCode" label="Postal code" outlined />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.trim="member.city" label="City" outlined />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-card-title class="section-title">Membership</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="member.team"
                                :items="teams"
                                item-text="title"
                                item-value="id"
                                label="Team"
                                prepend-inner-icon="mdi-account-group-outline"
                                :clearable="!readOnly"
                                return-object
                                outlined
                            />
                        </v-col>
                        <v-col v-if="!isDealer" cols="12" md="6">
                            <v-autocomplete
                                v-model="member.dealer"
                                :items="dealers"
                                :item-text="fullName"
                                item-value="id"
                                label="Dealer"
                                prepend-inner-icon="mdi-handshake-outline"
                                :clearable="!readOnly"
                                return-object
                                outlined
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <date-field v-model="member.subscriptionDate" label="Subscription date" :readonly="readOnly" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.recommendation" label="Recommendation" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="member.vipGroup" label="VIP group" outlined />
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex flex-wrap checkbox-row">
                                <v-checkbox v-model="member.vip" label="VIP" hide-details />
                                <v-checkbox v-model="member.reliable" label="Reliable" hide-details />
                                <v-checkbox v-model="member.exclusive" label="Exclusive" hide-details />
                                <v-checkbox v-model="member.vodafone" label="Vodafone" hide-details />
                                <v-checkbox v-model="member.energy" label="Energy" hide-details />
                                <v-checkbox v-model="member.newsletter" label="Newsletter" hide-details />
                                <template v-if="!isDealer">
                                    <v-checkbox v-model="member.approved" label="Approved" hide-details />
                                    <v-checkbox v-model="member.active" label="Active" hide-details />
                                </template>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-card-title class="section-title">Photo</v-card-title>
                <v-card-text>
                    <image-input v-model="image" :readonly="readOnly" />
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create member' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>

        <v-card v-if="!isCreate" outlined class="mt-6">
            <v-card-title class="section-title">
                Cards
                <v-chip x-small class="ml-2">{{ cards.length }}</v-chip>
                <v-spacer />
                <v-btn v-if="canEditCards" small outlined color="primary" @click="addCard">
                    <v-icon left small>mdi-plus</v-icon>Add card
                </v-btn>
            </v-card-title>
            <v-card-text>
                <div v-if="!cards.length" class="text--secondary">This member has no cards.</div>
                <v-expansion-panels v-model="openCard" accordion flat class="cards">
                    <v-expansion-panel v-for="(card, index) of cards" :key="card.id || `new-${index}`" class="card-panel">
                        <v-expansion-panel-header>
                            <div class="d-flex align-center flex-wrap">
                                <v-icon small class="mr-2">mdi-card-account-details-outline</v-icon>
                                <span class="font-weight-medium mr-2">{{ card.number || 'New card' }}</span>
                                <v-chip x-small outlined class="mr-2">{{ card.type }}</v-chip>
                                <v-chip v-if="card.id" x-small :color="card.active ? 'success' : 'grey'" :outlined="!card.active">
                                    {{ card.active ? 'Active' : 'Inactive' }}
                                </v-chip>
                                <v-chip v-else x-small color="warning" outlined>Not saved</v-chip>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <card-form
                                :member-id="memberId"
                                :member-card="card"
                                :readonly="!canEditCards"
                                @saved="onCardSaved(index, $event)"
                                @remove="removeCard(index)"
                            />
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
    import imageService from '@/services/image-service'
    import teamService from '@/services/team-service'
    import dealerService from '@/services/dealer-service'
    import memberService from '@/services/member-service'
    import cardService from '@/services/card-service'
    import formPage from '@/mixins/form-page'
    import imageUpload from '@/mixins/image-upload'
    import CardForm from '@/forms/CardForm'
    import PageHeader from '@/components/PageHeader'
    import DateField from '@/components/DateField'
    import ImageInput from '@/components/ImageInput'
    import { entityId, fullName, pick, toInputDate } from '@/utils/format'

    // MemberType clears every field missing from the payload, so always send all of them
    const MEMBER_FIELDS = [
        'firstName', 'lastName', 'birthDate', 'vip', 'reliable', 'exclusive', 'number', 'vipGroup', 'profession',
        'approved', 'phone', 'fatherName', 'email', 'subscriptionDate', 'vodafone', 'energy', 'recommendation',
        'address', 'postalCode', 'city', 'newsletter', 'gender', 'dealer', 'team', 'active', 'dimotologioNumber'
    ]

    function emptyMember() {
        return {
            firstName: '',
            lastName: '',
            fatherName: '',
            profession: '',
            birthDate: null,
            subscriptionDate: null,
            number: '',
            phone: '',
            email: '',
            address: '',
            postalCode: '',
            city: '',
            recommendation: '',
            vipGroup: '',
            dimotologioNumber: '',
            gender: null,
            vip: false,
            reliable: false,
            exclusive: false,
            vodafone: false,
            energy: false,
            newsletter: false,
            approved: true,
            active: true,
            team: null,
            dealer: null
        }
    }

    export default {
        name: 'MemberForm',
        components: { CardForm, PageHeader, DateField, ImageInput },
        mixins: [formPage, imageUpload],
        data: () => {
            return {
                entityLabel: 'Member',
                memberId: null,
                member: emptyMember(),
                cards: [],
                openCard: null,
                teams: [],
                dealers: [],
                genders: [
                    { text: 'Male', value: 'male' },
                    { text: 'Female', value: 'female' },
                    { text: 'Other', value: 'other' }
                ],
                emailRules: [
                    v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Enter a valid email address'
                ]
            }
        },
        computed: {
            isDealer() {
                return this.$store.getters.isDealer
            },
            canEditCards() {
                return this.isEdit && !this.isDealer
            },
            subtitle() {
                if (this.isCreate) {
                    return null
                }
                return [fullName(this.member), this.member.number ? `#${this.member.number}` : null].filter(Boolean).join(' · ')
            }
        },
        created() {
            teamService.getTeamOptions().then(resp => {
                this.teams = resp.data.data
            })
            if (!this.isDealer) {
                dealerService.getAllDealers().then(resp => {
                    this.dealers = resp.data.data
                })
            }
            if (this.isCreate) {
                return
            }
            this.memberId = parseInt(this.$route.params.memberId)
            this.loadMember()
        },
        methods: {
            fullName,
            loadMember() {
                this.loadingEntity = true
                return memberService.getMemberById(this.memberId).then(resp => {
                    const member = resp.data.data.member
                    this.member = {
                        ...emptyMember(),
                        ...member,
                        birthDate: toInputDate(member.birthDate),
                        subscriptionDate: toInputDate(member.subscriptionDate)
                    }
                    this.cards = member.cards || []
                    this.setExistingImage(member.image)
                }).catch(error => {
                    this.$notify.error(error, 'Could not load the member')
                }).finally(() => {
                    this.loadingEntity = false
                })
            },
            buildPayload() {
                const payload = pick(this.member, MEMBER_FIELDS)
                payload.team = entityId(this.member.team)
                payload.dealer = entityId(this.member.dealer)
                return payload
            },
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                const payload = this.buildPayload()
                const request = this.isEdit
                    ? memberService.editMemberById(this.memberId, payload)
                    : memberService.createMember(payload)
                request.then(resp => {
                    const id = this.isEdit ? this.memberId : resp.data.id
                    return this.saveImage('memberId', id, imageService.uploadNewImage)
                        .catch(error => this.$notify.error(error, 'The member was saved, but the photo could not be saved'))
                        .then(() => id)
                }).then(id => {
                    this.$notify.success(this.isCreate ? 'Member created – you can now add cards' : 'Member saved')
                    if (this.isCreate) {
                        // continue on the edit page so the member can get cards and a second save does not create a duplicate
                        this.goToEntity(id, 'edit')
                    } else {
                        this.loadMember()
                    }
                }).catch(error => {
                    this.$notify.error(error, 'Could not save the member')
                }).finally(() => {
                    this.saving = false
                })
            },
            addCard() {
                this.cards.push({ type: 'PARON', active: true })
                this.openCard = this.cards.length - 1
            },
            onCardSaved(index, card) {
                this.$set(this.cards, index, card)
            },
            removeCard(index) {
                const card = this.cards[index]
                if (!card.id) {
                    this.cards.splice(index, 1)
                    return
                }
                cardService.deleteCardById(card.id).then(() => {
                    this.cards.splice(index, 1)
                    this.openCard = null
                    this.$notify.success('Card removed')
                }).catch(error => {
                    this.$notify.error(error, 'Could not remove the card')
                })
            }
        }
    }
</script>

<style scoped>

.card-panel {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px !important;
    margin-bottom: 8px;
}

.card-panel::before {
    box-shadow: none !important;
}
</style>
